import type { MedRequest } from '@/types/data'
import { isRoleAllowedToUseDecisionAssistant } from '@/lib/permissions'
import { useFlag } from '@unleash/proxy-client-react'

type VisibilityParams = {
  daFlagEnabled: boolean
  patientOrigin?: 'medication-request' | 'investigation'
  isInvestigation?: boolean
  hasOnHoldHistory: boolean
  hasDaAccessRole: boolean
  isPomOrder: boolean
}

/**
 * DA suggestions are only offered on POM med requests — OTC (P) and GSL products
 * don't need a prescribing decision. Prefer the ordered product's group; fall back
 * to the `OTC` flag on payloads that don't carry it (the flag only marks P items).
 */
export function isPomMedRequest(medRequestData?: MedRequest | null): boolean {
  if (!medRequestData) return false
  const group = medRequestData.productGroup?.trim().toUpperCase()
  if (group) return group === 'POM'
  return !medRequestData.OTC
}

export function computeShowDecisionAssistant({
  daFlagEnabled,
  patientOrigin = 'medication-request',
  isInvestigation = false,
  hasOnHoldHistory,
  hasDaAccessRole,
  isPomOrder
}: VisibilityParams): boolean {
  return (
    daFlagEnabled &&
    hasDaAccessRole &&
    patientOrigin === 'medication-request' &&
    !isInvestigation &&
    !hasOnHoldHistory &&
    isPomOrder
  )
}

export function useDecisionAssistantVisibility({
  medRequestData,
  patientOrigin = 'medication-request',
  isInvestigation = false
}: {
  medRequestData?: MedRequest | null
  patientOrigin?: 'medication-request' | 'investigation'
  isInvestigation?: boolean
}) {
  const daFlagEnabled = useFlag('da-pa-suggestion-panel')
  const hasOnHoldHistory = Array.isArray(medRequestData?.onHoldHistory) && medRequestData.onHoldHistory.length > 0

  const showDecisionAssistant = computeShowDecisionAssistant({
    daFlagEnabled,
    patientOrigin,
    isInvestigation,
    hasOnHoldHistory,
    hasDaAccessRole: isRoleAllowedToUseDecisionAssistant(),
    isPomOrder: isPomMedRequest(medRequestData)
  })

  return { showDecisionAssistant, daFlagEnabled }
}
